import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

export const AuthModal: React.FC = () => {
    const { 
        isAuthModalOpen, 
        closeAuthModal, 
        authModalView,
        login, 
        register, 
        isLoading, 
        error 
    } = useAuth();
    
    const [view, setView] = useState(authModalView);
    
    // Form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    
    useEffect(() => {
        // Reset form and view when modal is opened
        if (isAuthModalOpen) {
            setView(authModalView);
            setName('');
            setEmail('');
            setPassword('');
        }
    }, [isAuthModalOpen, authModalView]);

    if (!isAuthModalOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (view === 'login') {
            await login(email, password);
        } else {
            await register(name, email, password);
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
            onClick={closeAuthModal} 
            role="dialog" 
            aria-modal="true" 
            aria-labelledby="auth-modal-title"
            style={{ animation: 'modal-fade-in 0.3s' }}
        >
            <div 
                className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md" 
                onClick={(e) => e.stopPropagation()}
                style={{ animation: 'modal-content-pop 0.3s' }}
            >
                <div className="p-6 sm:p-8">
                    <div className="flex justify-between items-start mb-4">
                        <h2 id="auth-modal-title" className="text-2xl font-bold text-slate-900 dark:text-white">
                            {view === 'login' ? 'Welcome Back' : 'Create Account'}
                        </h2>
                         <button onClick={closeAuthModal} className="text-gray-400 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white text-3xl leading-none" aria-label="Close modal">&times;</button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {view === 'register' && (
                            <div>
                                <label htmlFor="name-modal" className="block text-sm font-medium text-slate-700 dark:text-slate-300">Full Name</label>
                                <input type="text" id="name-modal" value={name} onChange={e => setName(e.target.value)} required autoComplete="name" className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"/>
                            </div>
                        )}
                        <div>
                            <label htmlFor="email-modal" className="block text-sm font-medium text-slate-700 dark:text-slate-300">Email Address</label>
                            <input type="email" id="email-modal" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"/>
                        </div>
                        <div>
                            <label htmlFor="password-modal"  className="block text-sm font-medium text-slate-700 dark:text-slate-300">Password</label>
                            <input type="password" id="password-modal" value={password} onChange={e => setPassword(e.target.value)} required autoComplete={view === 'login' ? 'current-password' : 'new-password'} className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"/>
                        </div>

                        {error && <p className="text-sm text-center text-red-500 bg-red-100 dark:bg-red-900/20 p-2 rounded-md">{error}</p>}
                        
                        <button type="submit" disabled={isLoading} className="w-full py-3 px-4 bg-cyan-500 text-white font-semibold rounded-full hover:bg-cyan-600 transition disabled:opacity-50 flex items-center justify-center">
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </>
                            ) : (view === 'login' ? 'Sign In' : 'Sign Up')}
                        </button>
                    </form>
                    
                    <p className="text-sm text-center text-slate-500 dark:text-slate-400 mt-6">
                        {view === 'login' ? "Don't have an account?" : "Already have an account?"}{' '}
                        <button onClick={() => setView(view === 'login' ? 'register' : 'login')} className="font-medium text-cyan-600 dark:text-cyan-400 hover:underline focus:outline-none">
                            {view === 'login' ? 'Sign up' : 'Sign in'}
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};
